import { isPlatformBrowser } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { AppConfigService, LoggingService } from '@com-delta-idp/mnx-utils';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { v4 as generateId } from 'uuid';
import { ApplicationConfig } from '../config/config';
import { getCorporatePartner } from '../corporate-partner/corporate-partner.method';
import { HTTP_TIMEOUT } from '../interceptor/timeout.interceptor';
import { DefaultLookUpSuccessResponse } from '../model/common-service.interface';
import { ContentAccessibilityPopup } from '../model/content/contentAccessibilityPopup.model';
import { SearchPERequest } from '../model/userdata-model';
import { CookieHelper } from './../common/cookie-helper';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  axPopupContent: ContentAccessibilityPopup;
  modalOnHiddenSubscription: Subscription;
  modalConfig = {
    animated: false
  };

  public reqActiveEle: any;
  public enableDragModal = true;
  focusableEls: any = [];
  firstFocusableEl: any = [];
  lastFocusableEl: any = [];
  lightFooter: boolean;
  shopHeader: boolean;
  isShoppingHeader: boolean;
  flyOutPositionHeight: any;
  flyoutFeedHeaderPosition: any;
  flyOutLocation: any;
  loginSessionRefreshVal: boolean;
  deepLinkVendorId = '';
  private observable: Observable<any>;
  private reqPort;
  private testPorts =
    '23011,23012,23013,23014,23015,23016,23017,23018,23019,23020';

  shoppingOptimization: boolean; // if this flag is true executing new changes for optimization else existing code flow

  removeSessionStorageKey = '';

  constructor(
    private http: HttpClient,
    private config: ApplicationConfig,
    private appConfigService: AppConfigService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookieHelper: CookieHelper,
    private loggingService: LoggingService
  ) {}

  setAxPopupContent(axPopupContent: ContentAccessibilityPopup) {
    this.axPopupContent = axPopupContent;
  }

  getAxPopupContent() {
    return this.axPopupContent;
  }

  getRssNews() {
    // if ('' !== this.reqPort && this.testPorts.search(this.reqPort) >= 0) {
    //   return null;
    // }
    if (isPlatformBrowser(this.platformId)) {
      return this.http
        .get<any>(`${this.config.get('APP_RSS_NEWS_URL')}`)
        .pipe(share());
    } else {
      return null;
    }
  }

  getDefaultsAndLookup(cacheKeySuffix?) {
    const defaultLookupUrl = this.config.get('APP_GET_DEFAULTS_AND_LOOKUPS');
    const newAWSDefaultLookupUrl = this.config.get(
      'APP_NEW_AWS_GET_DEFAULTS_AND_LOOKUPS'
    );

    if (defaultLookupUrl) {
      if (cacheKeySuffix) {
        const customheaders = new HttpHeaders({
          CacheKey: `${cacheKeySuffix}`
        });
        customheaders.append('Accept', 'application/json');
        return this.http
          .get<any>(`${defaultLookupUrl}`, {
            headers: customheaders
          })
          .pipe(share());
      } else {
        return this.http.get<any>(`${defaultLookupUrl}`).pipe(share());
      }
    } else {
      if (newAWSDefaultLookupUrl) {
        const uniqueId = cacheKeySuffix || generateId().toString();
        return (
          // from([DefaultLookUpSuccessResponseMock])
          this.http
            .get<DefaultLookUpSuccessResponse>(`${newAWSDefaultLookupUrl}`)
            .pipe(
              map((response: DefaultLookUpSuccessResponse) => {
                return {
                  searchCriteria: {
                    ...response,
                    request: {
                      pageName: response.request.Values[0].pageName,
                      cacheKey: uniqueId
                    },
                    fareBrandsByMoney: response.fareBrandsByMoney.Values,
                    journeyTypeOptions: response.journeyTypeOptions.Values,
                    disableBestFaresFor: response.disableBestFares
                  }
                };
              }),
              share()
            )
        );
      }
      const error = new Error('Defaults and lookup URL not found');
      return throwError(error).pipe(share());
    }
  }

  dragModalContainers(nativeData, rendererElement) {
    try {
      if (this.config.get('APP_DISABLE_LEGACY') === 'false') {
        let modalContainerData;
        let rootDocList;
        try {
          if (this.checkIEOrEdgeBrowser() === true) {
            rootDocList = document;
          } else {
            rootDocList = nativeData.nativeElement.ownerDocument;
          }
        } catch (e) {
          rootDocList = document.querySelector('body');
        }
        const freshAirContainer = rootDocList.querySelector('header');
        const bsBackdrop = rootDocList.querySelector('bs-modal-backdrop');
        if (rootDocList.getElementsByTagName('modal-container').length > 1) {
          modalContainerData =
            rootDocList.getElementsByTagName('modal-container')[1];
        } else {
          modalContainerData = rootDocList.querySelector('modal-container');
        }
        if (modalContainerData && bsBackdrop) {
          rendererElement.addClass(bsBackdrop, 'show');
          rendererElement.addClass(modalContainerData, 'show');
          rendererElement.addClass(modalContainerData, 'fresh-air');
          rendererElement.appendChild(freshAirContainer, bsBackdrop);
          rendererElement.appendChild(freshAirContainer, modalContainerData);
          const modalContainerChild = modalContainerData.children[0];
          if (modalContainerChild) {
            let footerExist;
            rendererElement.removeClass(modalContainerChild, 'footer');
            if (modalContainerChild.classList.value) {
              footerExist =
                modalContainerChild.classList.value.includes('hp-footer');
            }
            // else {
            //   footerExist = modalContainerChild.classList.toString().includes('hp-footer');
            // }
            const searchModalContainerFooter = modalContainerData.querySelector(
              '.footer-search-modal'
            );
            if (footerExist) {
              rendererElement.removeClass(modalContainerChild, 'hp-footer');
            }
            if (searchModalContainerFooter) {
              rendererElement.addClass(modalContainerChild, 'hp-footer');
            }
            rendererElement.addClass(modalContainerData, 'd-block');
          }
        }
      }
    } catch (err) {
      this.loggingService.log('Caught Error: ' + err);
    }
  }

  removeMultipleContainers(nativeData) {
    try {
      if (this.config.get('APP_DISABLE_LEGACY') === 'false') {
        let rootDocList;
        try {
          if (this.checkIEOrEdgeBrowser() === true) {
            rootDocList = document;
          } else {
            rootDocList = nativeData.nativeElement.ownerDocument;
          }
        } catch (e) {
          rootDocList = document.querySelector('body');
        }
        if (rootDocList.getElementsByTagName('modal-container').length > 1) {
          const modalLen =
            rootDocList.getElementsByTagName('modal-container').length;
          for (let i = 1; i < modalLen; i++) {
            const curModalEle =
              rootDocList.getElementsByTagName('modal-container')[i];
            if (this.checkIEOrEdgeBrowser() === true) {
              curModalEle.parentNode.removeChild(curModalEle);
            } else {
              curModalEle.remove();
            }
          }
        }
        if (rootDocList.getElementsByTagName('bs-modal-backdrop').length > 1) {
          const bsBackdrpLen =
            rootDocList.getElementsByTagName('bs-modal-backdrop').length;
          for (let i = 1; i < bsBackdrpLen; i++) {
            const curBackdropEle =
              rootDocList.getElementsByTagName('bs-modal-backdrop')[i];
            if (this.checkIEOrEdgeBrowser() === true) {
              curBackdropEle.parentNode.removeChild(curBackdropEle);
            } else {
              curBackdropEle.remove();
            }
          }
        }
      }
    } catch (err) {
      this.loggingService.log('Caught Error: ' + err);
    }
  }

  focusModalContainers(nativeData, rendererElement) {
    if (this.config.get('APP_DISABLE_LEGACY') === 'false') {
      let rootDocList;
      try {
        if (this.checkIEOrEdgeBrowser() === true) {
          rootDocList = document;
        } else {
          rootDocList = nativeData.nativeElement.ownerDocument;
        }
      } catch (e) {
        rootDocList = document.querySelector('body');
      }
      if (rootDocList) {
        const modalContainerData = rootDocList.querySelector('modal-container');
        const searchModalContainer =
          modalContainerData.querySelector('.search-flyout');
        if (searchModalContainer !== null) {
          searchModalContainer.querySelector('#search_input').focus();
        } else {
          modalContainerData.focus();
        }
      }
    }
  }

  checkPath() {
    return window.location.pathname.indexOf('/')
      ? '/' + window.location.pathname
      : window.location.pathname;
  }

  checkEmptyNullUndefined(dataValue) {
    if (dataValue !== null && dataValue !== undefined && dataValue !== '') {
      return true;
    } else {
      return false;
    }
  }

  trapFocusInsideModal(el) {
    const activeModalElement = el.nativeElement.children[0];
    if (activeModalElement) {
      const tabbableEls = activeModalElement.querySelectorAll(`a[href],
      area[href],
      input:not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      button:not([disabled]),
      [tabindex="0"]`);
      const filter = Array.prototype.filter;
      this.modalElementsFocus(filter, tabbableEls, activeModalElement);
      this.focusableEls = Array.prototype.slice.call(this.focusableEls);
      this.firstFocusableEl = this.focusableEls[0];
      this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];
      this.focusableEls = activeModalElement.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      );
      const KEYCODE_TAB = 9;
      this.modalTabEvent(KEYCODE_TAB, activeModalElement);
    }
  }

  modalElementsFocus(filter, tabbableEls, activeModalElement) {
    this.focusableEls = filter.call(tabbableEls, (el) => {
      let elCopy = el;
      while (elCopy !== activeModalElement) {
        const displayVal = window
          .getComputedStyle(elCopy, null)
          .getPropertyValue('display');
        const visibilityVal = window
          .getComputedStyle(elCopy, null)
          .getPropertyValue('visibility');
        if (displayVal !== 'none' && visibilityVal !== 'hidden') {
          elCopy = elCopy.parentNode;
        } else {
          return false;
        }
      }
      return true;
    });
  }

  modalTabEvent(KEYCODE_TAB, activeModalElement) {
    activeModalElement.addEventListener('keydown', (e: any) => {
      if (e.key === 'Tab' || e.keyCode === KEYCODE_TAB) {
        if (e.shiftKey) {
          if (document.activeElement === this.firstFocusableEl) {
            this.lastFocusableEl.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === this.lastFocusableEl) {
            this.firstFocusableEl.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  doesClassExists(...args: any[]): boolean {
    const classArr: any = [];
    args.forEach(function (value) {
      const classExistVal =
        document.getElementsByClassName(value)?.length > 0 ? true : false;
      classArr.push(classExistVal);
    });

    const checker = classArr.some(Boolean);
    return checker;
  }

  splitCookieString(cookie: any) {
    if (cookie !== null) {
      return cookie.split('=')[1].toString();
    }
  }

  splitCookieArray(cookie: any) {
    if (cookie !== null) {
      return cookie.split('=');
    }
  }

  hidingProfileSelector() {
    const hidingProfileSelectorURLS = this.config.get(
      'APP_RESTRICTED_PAGES_FOR_BUSINESS'
    )
      ? this.config.get('APP_RESTRICTED_PAGES_FOR_BUSINESS').split(',')
      : null;
    const url = window.location.pathname;
    const LOGIN_PICKER_IN_RESHOP_CONFIRMATION = [
      '/nextgencheckout/change-trip.html/confirmation',
      '/change-trip/confirmation'
    ];
    LOGIN_PICKER_IN_RESHOP_CONFIRMATION.forEach((val) => {
      if (url.includes(val)) {
        return false;
      }
    });
    if (hidingProfileSelectorURLS) {
      for (const x of hidingProfileSelectorURLS) {
        const y = url.indexOf(x);
        if (url.indexOf(x) !== -1) {
          return true;
        }
      }
    }
    return false;
  }

  getPEDataForSearch(peRequest: SearchPERequest) {
    const peUrl = `${this.config.get('APP_SFAF_PERSONALIZATION_ENGINE_URL')}`;
    const body = JSON.stringify(peRequest);
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    return this.http.post(peUrl, body, {
      headers: customheaders,
      responseType: 'json'
    });
  }

  checkLightHeaderFooter() {
    if (this.config?.get('APP_HEADERFOOTER_LIGHT_WEIGHT') === 'true') {
      this.lightFooter = true;
    } else {
      this.lightFooter = false;
    }
    return this.lightFooter;
  }

  checkShopHeader() {
    if (this.config.get('APP_SHOP_HEADER') === 'true') {
      this.shopHeader = true;
    } else {
      this.shopHeader = false;
    }
    return this.shopHeader;
  }

  async getPfCookieValue(): Promise<string> {
    if (isPlatformBrowser(this.platformId)) {
      const response: string = await this.http
        .get<any>(`${this.config.get('APP_PF_COOKIE_RETRIEVE')}`)
        .toPromise();
      return response;
    }
  }

  getSessCustData() {
    // const body = JSON.stringify(peRequest);
    const customerHeader = new HttpHeaders({
      'Content-type': 'application/json; charset=utf-8'
    });
    customerHeader.append('Accept', 'application/json');
    // let body = {"userdata": { "origin": "JUH","destination":"JFK"}, "requestparam": { "campaignId": "shopping-lite:dynamic-banner","format":"html"} };
    return this.http.post(
      `${this.config.get('APP_SESS_CUSTOMER_DATA_URL')}`,
      {},
      { headers: customerHeader, responseType: 'json' }
    );
  }

  flyoutPosition(type, flyoutLoaded) {
    if (isPlatformBrowser(this.platformId) && document && document.body) {
      /*let clntwidth;
    if ( type === 'search' ) {
    clntwidth = 991;
    } else {
      clntwidth = 767;
    }*/
      if (document.body.clientWidth > 991) {
        this.isShoppingHeader = this.checkShopHeader();
        const alertAdvisoryEl = document.body.querySelector(
          '.alert-advisory-main-container'
        );
        let alertAdvisoryHeight;
        if (alertAdvisoryEl !== null && alertAdvisoryEl !== undefined) {
          alertAdvisoryHeight = alertAdvisoryEl.clientHeight;
        } else {
          alertAdvisoryHeight = 0;
        }
        let navBarEl;
        if (this.isShoppingHeader) {
          if (document.body.querySelector('.shop-header-view')) {
            navBarEl = document.body.querySelector('.shop-header-view');
          } else if (document.body.querySelector('.navbar-expand-lg')) {
            navBarEl = document.body.querySelector('.navbar-expand-lg');
          }
        } else {
          navBarEl = document.body.querySelector('.navbar-expand-lg');
        }
        let navBarHeight;
        if (navBarEl !== null && navBarEl !== undefined) {
          navBarHeight = navBarEl.clientHeight;
        } else {
          navBarHeight = 0;
        }
        this.flyOutPositionHeight = alertAdvisoryHeight + navBarHeight;
      } else if (type === 'logged-in' || type === 'feed') {
        this.flyOutPositionHeight = 46;
        const feedHeader = document.getElementsByClassName('feed-header')[0];
        if (feedHeader !== undefined && feedHeader !== null && flyoutLoaded) {
          feedHeader.classList.add('feed-header-fixed');
        } else {
          this.flyoutFeedHeaderPosition = -46;
        }
      } else if (type === 'search') {
        this.flyOutPositionHeight = 45;
        this.flyOutLocation = 'relative';
      } else if (type === 'logged-in user') {
        this.flyOutPositionHeight = 46;
      }
    }
    const flyoutObj = {
      flyOutPositionHeight: this.flyOutPositionHeight,
      flyoutFeedHeaderPosition: this.flyoutFeedHeaderPosition,
      flyOutLocation: this.flyOutLocation
    };
    return flyoutObj;
  }

  setLoginSessionRefreshVal(sessRefreshVal) {
    this.loginSessionRefreshVal = sessRefreshVal;
  }

  getLoginSessionRefreshVal() {
    return this.loginSessionRefreshVal;
  }

  checkIEOrEdgeBrowser() {
    if (isPlatformBrowser(this.platformId)) {
      const isIEOrEdge = /msie\s|trident\/|edge\//i.test(
        window.navigator.userAgent
      );
      if (isIEOrEdge) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Connects to the /v2/paymentDetails URL and receives the observable response
   */
  savePaymentDetails(requestData): Observable<any> {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const paymentAWS =
      this.appConfigService.getConfig('HEADER') &&
      this.appConfigService.getConfig('HEADER')['PAYMENT_AWS'];
    const app_content_config_url =
      paymentAWS || this.config.get('APP_SAVE_PAYMENT_URL');
    let headers = new HttpHeaders();
    headers = headers.set(HTTP_TIMEOUT, svcTimeOut.toString());
    headers = headers.set('Content-Type', 'application/json; charset=UTF-8');
    headers = headers.set('appId', 'WEB');
    headers = headers.set('channelId', 'DCOM');
    headers = headers.set('purchasePath', 'MYTRIPS');
    const requestOptions = { headers: headers, params: new HttpParams() };
    const headerfooterresp = this.http
      .post(`${app_content_config_url}`, requestData, requestOptions)
      .pipe(catchError(this.handleError));
    return headerfooterresp;
  }

  getCorpPartner(partnerId, corpPartner, cacheKeySuffix) {
    const vendor = this.cookieHelper.getDeepLinkVendorValue(cacheKeySuffix);
    if (
      vendor &&
      (partnerId === 'default' || partnerId === 'personal') &&
      this.config.get('APP_DEEP_LINK_THEME') &&
      'true' === this.config.get('APP_DEEP_LINK_THEME')
    ) {
      return getCorporatePartner(vendor);
    } else {
      return corpPartner;
    }
  }

  getDeepLinkVendor() {
    if (this.config.get('APP_DEEP_LINK_VENDOR')) {
      return this.config.get('APP_DEEP_LINK_VENDOR');
    } else {
      return '';
    }
  }

  setDeepLinkVendorCookie(vendorId, cacheKeySuffix) {
    if (vendorId && vendorId === this.getDeepLinkVendor()) {
      this.cookieHelper.setDeepLinkCookie(
        'deepLinkVendor' + cacheKeySuffix,
        vendorId
      );
      this.cookieHelper.setDeepLinkCookie('deepLinkCacheKey', cacheKeySuffix);
    } else if (vendorId && vendorId !== this.getDeepLinkVendor()) {
      this.cookieHelper.deleteCookie('deepLinkVendor');
    }
  }

  removeDeepLinkVendorCookie() {
    if (!this.config.get('APP_DEEP_LINK_VENDOR')) {
      this.cookieHelper.deleteCookie('deepLinkVendor');
    }
  }

  setVendorId(vendorId) {
    const configVendorId = this.getDeepLinkVendor();
    this.deepLinkVendorId = configVendorId === vendorId ? vendorId : '';
  }

  async getConfigFromAem(): Promise<OauthAEMConfig> {
    let response: OauthAEMConfig;
    const configUrl = this.config?.get('APP_AUTH_CONFIG_JSON') ?? '';
    if (configUrl) {
      response = await this.http
        .get<OauthAEMConfig>(`${this.config.get('APP_AUTH_CONFIG_JSON')}`)
        .toPromise();
    } else {
      response = null;
    }
    return response;
  }

  getPingLogOutUrl(authConfig: OauthAEMConfig): string {
    const envFromConfig = this.config.get('APP_ENV');
    let pingDomain = '';
    if (authConfig) {
      if (!envFromConfig) {
        pingDomain = authConfig.ISSUER_PROD;
      } else {
        pingDomain = this.config.get('APP_DYNAMIC_ENV_FROM_AUTH_JSON')
          ? authConfig.PING_DOMAIN
          : authConfig.ISSUER[envFromConfig];
      }
      return `${pingDomain}${authConfig.LOG_OUT_URL}${window.location.protocol}//${window.location.hostname}`;
    }
  }

  // TODO move this to a common service util
  private handleError(error: HttpErrorResponse) {
    if (
      isPlatformBrowser(this.platformId) &&
      error.error instanceof ErrorEvent
    ) {
      // A client-side or network error occurred. Handle it accordingly.
      this.loggingService.log('An error occurred:' + error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    // return an ErrorObservable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }

  removeSessionStorageAfterLogout() {
    if (this.removeSessionStorageKey) {
      sessionStorage.removeItem(this.removeSessionStorageKey);
    }
  }
}
